import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import {
  doc,
  collection,
  query,
  orderBy,
  limit,
  getDoc,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { auth } from "../firebase";
import PayPalButtonBase from "./PayPalButtonBase";

export default function PayProfileModal({
  isOpen,
  onClose,
  promoProfiles,
  userId,
  clientId,
  exchangeRate,
  readyToRenderPayPal,
  setReadyToRenderPayPal,
  orderId,
  setOrderId,
  paypalError,
  setPaypalError,
  processing,
  setProcessing,
  onSuccess,
  onError,
}) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [transactionData, setTransactionData] = useState({});
  const [activeProfileId, setActiveProfileId] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const getLatestTransactionData = async (profileId) => {
    let centralizedExpirationDate = null;
    let amountUSD = null;
    let planType = null;
    let postsPerMonth = null;

    try {
      const profileRef = doc(db, `users/${userId}/promoProfiles/${profileId}`);
      const profileDoc = await getDoc(profileRef);

      if (profileDoc.exists()) {
        const profileData = profileDoc.data();
        centralizedExpirationDate = profileData.centralizedExpirationDate?.toDate() || null;
        amountUSD = profileData.amountUSD || null;
        planType = profileData.planType || null;
        postsPerMonth = profileData.postsPerMonth || null;
      }
    } catch (error) {
      console.error("Error obteniendo datos del perfil:", error);
    }

    try {
      const transactionsQuery = query(
        collection(db, `users/${userId}/promoProfiles/${profileId}/transactions`),
        orderBy("expirationDate", "desc"),
        limit(1)
      );
      const transactionsSnapshot = await getDocs(transactionsQuery);

      if (!transactionsSnapshot.empty) {
        const latestTransaction = transactionsSnapshot.docs[0].data();
        return {
          expirationDate: centralizedExpirationDate || latestTransaction.expirationDate?.toDate() || null,
          amountUSD: amountUSD || latestTransaction.amount || null,
          postsPerMonth: postsPerMonth || latestTransaction.postsPerMonth || null,
          planType: planType || latestTransaction.planType || null,
        };
      }
    } catch (error) {
      // transactions subcollection may not have a security rule yet — return profile data
    }

    return {
      expirationDate: centralizedExpirationDate || null,
      amountUSD: amountUSD || null,
      postsPerMonth: postsPerMonth || null,
      planType: planType || null,
    };
  };

  useEffect(() => {
    const fetchTransactionData = async () => {
      if (!isOpen || !userId || !promoProfiles.length) return;

      const data = {};
      for (const profile of promoProfiles) {
        const latestData = await getLatestTransactionData(profile.id);
        data[profile.id] = latestData;
      }
      setTransactionData(data);
    };

    fetchTransactionData();
  }, [isOpen, userId, promoProfiles]);

  useEffect(() => {
    if (!isOpen) {
      setReadyToRenderPayPal(false);
      setOrderId(null);
      setActiveProfileId(null);
      setPaypalError(null);
      setSuccessMessage(null);
    }
  }, [isOpen, setReadyToRenderPayPal, setOrderId, setPaypalError]);

  const formatExpirationDate = (date) => {
    if (!date) return "No disponible";
    return date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleProfilePayment = async (profile) => {
    if (processing || (activeProfileId === profile.id && readyToRenderPayPal)) return;

    setProcessing(true);
    setPaypalError(null);
    setActiveProfileId(profile.id);
    setReadyToRenderPayPal(false);
    setOrderId(null);

    try {
      const transaction = transactionData[profile.id];
      const planType = transaction?.planType || profile.planType;
      const amountUSD = transaction?.amountUSD || (planType === "basic" ? 500 / exchangeRate : 1000 / exchangeRate);
      const latestExpirationDate = transaction?.expirationDate;

      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${backendUrl}/api/renewals/create-profile-payment-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          amount: Number(amountUSD.toFixed(2)),
          userId,
          planType,
          profileId: profile.id,
          currentExpirationDate: latestExpirationDate ? latestExpirationDate.toISOString() : null,
        }),
      });

      const data = await response.json();
      if (data.orderId) {
        setOrderId(data.orderId);
        setReadyToRenderPayPal(true);
      } else {
        setPaypalError(data.error || "Error al crear la orden de pago.");
        setActiveProfileId(null);
      }
    } catch (error) {
      setPaypalError("Error al conectar con el servidor.");
      setActiveProfileId(null);
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentSuccess = (profile, orderDetails) => {
    // El servidor ya escribió la transacción y actualizó el perfil en Firestore
    // durante la captura (capture-profile-payment-order). Solo actualizamos la UI.
    const planType = orderDetails.planType || transactionData[profile.id]?.planType || profile.planType;
    const postsPerMonth = planType === "basic" ? 5 : 10;
    const amountUSD = transactionData[profile.id]?.amountUSD || (planType === "basic" ? 500 / exchangeRate : 1000 / exchangeRate);

    setTransactionData((prev) => ({
      ...prev,
      [profile.id]: {
        ...prev[profile.id],
        expirationDate: orderDetails.expirationDate ? new Date(orderDetails.expirationDate) : new Date(),
        amountUSD: Number(amountUSD.toFixed(2)),
        postsPerMonth,
        planType,
      },
    }));

    setSuccessMessage(`¡Pago exitoso para el perfil ${profile.name}!`);
    onSuccess();
    setReadyToRenderPayPal(false);
    setOrderId(null);
    setActiveProfileId(null);
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={() => !processing && onClose()}>
        <div onClick={handleModalClick}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/60 dark:bg-black/80" />
          </Transition.Child>

          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="scale-95 opacity-0"
              enterTo="scale-100 opacity-100"
              leave="ease-in duration-200"
              leaveFrom="scale-100 opacity-100"
              leaveTo="scale-95 opacity-0"
            >
              <Dialog.Panel className="w-full max-w-lg sm:max-w-2xl bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl p-6 sm:p-8 shadow-2xl transition-all">
                <button
                  onClick={() => !processing && onClose()}
                  className="absolute top-4 right-4 text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-300 text-2xl font-bold transition-transform hover:scale-125"
                >
                  ×
                </button>

                <Dialog.Title className="text-3xl sm:text-4xl font-extrabold text-center mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-indigo-300 dark:to-purple-400">
                  Pagar Perfiles Promocionales
                </Dialog.Title>

                {successMessage && (
                  <p className="text-center text-green-500 font-semibold text-lg mb-6 bg-green-100/50 dark:bg-green-900/50 py-2 rounded-lg z-50">
                    {successMessage}
                  </p>
                )}

                {promoProfiles.length === 0 ? (
                  <div className="text-center text-gray-600 dark:text-gray-300">
                    No tienes perfiles promocionales para pagar.
                  </div>
                ) : (
                  <div className="space-y-6">
                    {promoProfiles.map((profile) => {
                      const t = transactionData[profile.id];
                      const isExpiringSoon =
                        t?.expirationDate &&
                        (t.expirationDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24) <= 7;
                      const amountUSD = t?.amountUSD || (profile.planType === "basic" ? 500 / exchangeRate : 1000 / exchangeRate);
                      const amountDOP = t?.amountUSD ? Math.round(t.amountUSD * exchangeRate) : (profile.planType === "basic" ? 500 : 1000);

                      return (
                        <div
                          key={profile.id}
                          className={`p-6 rounded-2xl border-2 ${
                            isExpiringSoon ? "border-red-500" : "border-gray-200 dark:border-gray-700"
                          } bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/50 dark:to-purple-900/50`}
                          onClick={handleModalClick}
                        >
                          {profile.profilePhotoUrl && (
                            <img
                              src={profile.profilePhotoUrl}
                              alt={profile.name}
                              className="w-16 h-16 rounded-full mb-4 mx-auto border-2 border-white dark:border-gray-900 shadow-sm"
                            />
                          )}
                          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2 text-center">
                            {profile.name}
                          </h3>
                          <p className="text-gray-600 dark:text-gray-300 mb-2 text-center">{profile.description}</p>
                          <p className="text-gray-600 dark:text-gray-300 mb-2">
                            Plan: {(t?.planType || profile.planType) === "basic" ? "Básico" : "Premium"} ({t?.postsPerMonth || (profile.planType === "basic" ? 5 : 10)} posts/mes)
                          </p>
                          <p className="text-gray-600 dark:text-gray-300 mb-2">
                            Expira: {formatExpirationDate(t?.expirationDate)}{" "}
                            {isExpiringSoon && <span className="text-red-500 font-semibold">(Expira pronto)</span>}
                          </p>
                          <p className="text-gray-600 dark:text-gray-300 mb-2">
                            Tags: {profile.tags?.join(", ") || "Ninguno"}
                          </p>
                          <p className="text-gray-600 dark:text-gray-300 mb-4">
                            Costo: RD${amountDOP} (${Number(amountUSD.toFixed(2))} USD)
                          </p>
                          {readyToRenderPayPal && orderId && activeProfileId === profile.id ? (
                            <PayPalButtonBase
                              key={`${orderId}-${profile.id}`}
                              clientId={clientId}
                              createEndpoint="/api/renewals/create-profile-payment-order"
                              captureEndpoint="/api/renewals/capture-profile-payment-order"
                              createBody={{ amount: Number(amountUSD.toFixed(2)), months: 1, userId, planType: t?.planType || profile.planType, profileCount: 0, profileId: profile.id }}
                              captureBody={{ userId, amount: Number(amountUSD.toFixed(2)), planType: t?.planType || profile.planType, profileCount: 0, profileId: profile.id }}
                              onSuccess={(orderDetails) => handlePaymentSuccess(profile, orderDetails)}
                              onError={onError}
                              dedupeOrders={true}
                            />
                          ) : (
                            <button
                              onClick={() => handleProfilePayment(profile)}
                              disabled={processing || (readyToRenderPayPal && activeProfileId !== profile.id)}
                              className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-bold text-lg hover:from-indigo-600 hover:to-purple-700 transition-all disabled:opacity-50"
                            >
                              {processing && activeProfileId === profile.id ? (
                                <span className="flex items-center justify-center gap-3">
                                  <svg className="animate-spin h-6 w-6 text-white" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                  </svg>
                                  Procesando...
                                </span>
                              ) : (
                                "Pagar Perfil"
                              )}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {paypalError && (
                  <p className="text-center text-red-500 font-semibold text-lg mt-6 bg-red-100/50 dark:bg-red-900/50 py-2 rounded-lg z-50">
                    {paypalError}
                  </p>
                )}

                <button
                  onClick={() => !processing && onClose()}
                  className="w-full mt-8 text-gray-500 dark:text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-300 transition-colors text-base font-medium"
                >
                  Cancelar
                </button>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}